<template>
  <div class="scenarios-berekening flex-col gap-md">
    <!-- Display the fetched scenario input values -->
    <dd-header-card v-if="calculationValues" header="Uitgangspunten">
      <div class="flex-col gap-md">
        <div>
          <h3>Hoofdklant</h3>
          <dd-card-stacked-list
            :list="{
              ['Geboortedatum']: toDate(
                calculationValues.applicant.date_of_birth
              ),
              ['Inkomen uit werk']: toCurrency(
                calculationValues.applicant.income.annual_employment_income,
                0
              ),
              ['Inkomen uit AOW']: toCurrency(
                calculationValues.applicant.income.old_age_pension_income,
                0
              ),
              ['Inkomen uit werkgeverspensioen']: toCurrency(
                calculationValues.applicant.income.state_pension_income,
                0
              ),
              ['Inkomen uit nabestaandenpensioen']: toCurrency(
                calculationValues.applicant.income.survivor_pension_income,
                0
              ),
            }"
          />
        </div>
        <div>
          <h3 v-if="calculationValues.partner">Partner</h3>
          <dd-card-stacked-list
            v-if="calculationValues.partner"
            :list="{
              ['Geboortedatum']: toDate(
                calculationValues.partner.date_of_birth
              ),
              ['Inkomen uit werk']: toCurrency(
                calculationValues.partner.income.annual_employment_income,
                0
              ),
              ['Inkomen uit AOW']: toCurrency(
                calculationValues.partner.income.old_age_pension_income,
                0
              ),
              ['Inkomen uit werkgeverspensioen']: toCurrency(
                calculationValues.partner.income.state_pension_income,
                0
              ),
              ['Inkomen uit nabestaandenpensioen']: toCurrency(
                calculationValues.partner.income.survivor_pension_income,
                0
              ),
            }"
          />
        </div>
        <div>
          <h3>Woninggegevens</h3>
          <dd-card-stacked-list
            :list="{
              ['WOZ waarde']: toCurrency(calculationValues.property_tax_value),
            }"
          />
        </div>
        <!-- Leningsdelen -->
        <div>
          <h3 class="">Leningsdelen</h3>
          <p>
            In deze berekenen betrekken we alle hypotheken die gekoppeld zijn
            aan het huidige woonadres.
          </p>
          <dd-main-table
            rounded
            :rows="calculationValues.loan_parts"
            :headers="loanPartColumns"
            :has-select="false"
          >
          </dd-main-table>
        </div>
        <a-button
          type="primary"
          @click="calculateScenarios"
          :loading="calcLoading"
          style="align-self: flex-start"
        >
          Bereken
        </a-button>
        <small v-if="scenariosResult">
          Laatst berekend op {{ scenariosResult.calculatedAt | toDate }}
        </small>
      </div>
    </dd-header-card>

    <!-- Display the result of the calculation -->
    <dd-header-card v-if="calculationValues" header="Resultaat">
      <dd-loading-indicator v-if="calcLoading"></dd-loading-indicator>
      <div v-else>
        <div class="flex-col gap-md" v-if="scenariosToShow.length">
          <div>
            <dd-button :loading="downloadingPDF" @click="exportPDF"
              >Download PDF</dd-button
            >
          </div>
          <!-- The row of scenario cards -->
          <div class="flex-row gap-md">
            <div
              class="flex-stretch"
              v-for="scenarioKey in scenariosToShow"
              :key="scenarioKey"
            >
              <dd-card
                :class="{ active: activeScenario === scenarioKey }"
                @click="activeScenario = scenarioKey"
                size="small"
                style="cursor: pointer"
              >
                <div
                  class="d-flex flex-col justify-content-center align-items-center gap-sm"
                >
                  <dd-icon
                    v-if="
                      scenariosResult[scenarioKey].applicant.verdict.livable
                    "
                    icon="mdi mdi-check color-success"
                    size="xl"
                  />
                  <dd-icon
                    v-else
                    icon="mdi mdi-close-circle color-danger"
                    size="lg"
                  />
                  <h3 style="margin: 4px 0 0 0">
                    {{ scenarios[scenarioKey] }}
                  </h3>
                </div>
              </dd-card>
            </div>
          </div>

          <!-- Scenario details (only show if we have an active scenario) -->
          <div v-if="activeScenarioData" class="flex-col gap-md">
            <div class="flex-col gap-sm">
              <h3>Hoofdklant</h3>
              <div
                class="mt-3 mb-3"
                style="display: flex; align-items: center"
                v-if="scenarioEffectDescription[activeScenario]"
              >
                <span>
                  {{ scenarioEffectDescription[activeScenario] }}
                  <strong v-if="activeScenarioData.applicant.verdict.livable">
                    wel
                  </strong>
                  <strong v-else> niet </strong>
                  blijven wonen
                </span>
              </div>

              <dd-custom-table style="border-collapse: collapse">
                <tbody>
                  <tr>
                    <td colspan="2">Datum:</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.applicant.mortgage_situation.date
                          | toDate
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">Netto maandinkomen</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.applicant.income
                          .total_net_monthly_income | toCurrency(0)
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">Verschil met huidig netto inkomen</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.applicant.income
                          .difference_net_monthly_income | toCurrency(0)
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Huidige maandlast:</td>
                    <th>
                      {{
                        activeScenarioData.applicant.mortgage_situation
                          .monthly_payments | toCurrency(0)
                      }}
                    </th>
                    <td>Huidige restsom:</td>
                    <th>
                      {{
                        activeScenarioData.applicant.mortgage_situation
                          .remaining_sum | toCurrency(0)
                      }}
                    </th>
                  </tr>
                  <tr>
                    <td>Maandlast maximale hypotheek:</td>
                    <th>
                      {{
                        activeScenarioData.applicant.maximum_mortgage
                          .monthly_payments | toCurrency(0)
                      }}
                    </th>
                    <td>Bedrag maximale hypotheek:</td>
                    <th>
                      {{
                        activeScenarioData.applicant.maximum_mortgage.total_sum
                          | toCurrency(0)
                      }}
                    </th>
                  </tr>

                  <tr>
                    <td>Verschil:</td>
                    <th class="color-red">
                      {{
                        (activeScenarioData.applicant.maximum_mortgage
                          .monthly_payments -
                          activeScenarioData.applicant.mortgage_situation
                            .monthly_payments)
                          | toCurrency(0)
                      }}
                    </th>
                    <td>Verschil:</td>
                    <th>
                      {{
                        (activeScenarioData.applicant.maximum_mortgage
                          .total_sum -
                          activeScenarioData.applicant.mortgage_situation
                            .remaining_sum)
                          | toCurrency(0)
                      }}
                    </th>
                  </tr>
                  <tr>
                    <td>Kan betalen:</td>
                    <th>
                      <a-icon
                        v-if="
                          activeScenarioData.applicant.verdict.can_pay_mortgage
                        "
                        icon="mdi mdi-check color-success"
                        size="lg"
                      />
                      <dd-icon
                        v-else
                        icon="mdi mdi-close-circle color-danger"
                        size="lg"
                      />
                      {{
                        activeScenarioData.applicant.verdict.can_pay_mortgage
                          ? "Ja"
                          : "Nee"
                      }}
                    </th>
                    <td>Kan verlengen:</td>
                    <th>
                      <dd-icon
                        v-if="
                          activeScenarioData.applicant.verdict
                            .can_prolong_mortgage
                        "
                        icon="mdi mdi-check color-success"
                        size="lg"
                      />
                      <dd-icon
                        v-else
                        icon="mdi mdi-close-circle color-danger"
                        size="lg"
                      />
                      {{
                        activeScenarioData.applicant.verdict
                          .can_prolong_mortgage
                          ? "Ja"
                          : "Nee"
                      }}
                    </th>
                  </tr>
                </tbody>
              </dd-custom-table>
            </div>

            <!-- Partner Column -->
            <div
              v-if="
                activeScenarioData.partner && activeScenarioData.partner.verdict
              "
              class="flex-col gap-sm"
            >
              <h3>Partner</h3>
              <div
                class="mt-3 mb-3"
                style="display: flex; align-items: center"
                v-if="scenarioEffectDescription[activeScenario + 'Partner']"
              >
                <span>
                  {{ scenarioEffectDescription[activeScenario + "Partner"] }}
                  <strong v-if="activeScenarioData.partner.verdict.livable">
                    wel
                  </strong>
                  <strong v-else> niet </strong>
                  blijven wonen
                </span>
              </div>

              <dd-custom-table style="border-collapse: collapse">
                <tbody>
                  <tr>
                    <td colspan="2">Datum:</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.partner.mortgage_situation.date
                          | toDate
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">Netto maandinkomen</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.partner.income
                          .total_net_monthly_income | toCurrency(0)
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">Verschil met huidig netto inkomen</td>
                    <td colspan="2">
                      {{
                        activeScenarioData.partner.income
                          .difference_net_monthly_income | toCurrency(0)
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Huidige maandlast:</td>
                    <th>
                      {{
                        activeScenarioData.partner.mortgage_situation
                          .monthly_payments | toCurrency(0)
                      }}
                    </th>
                    <td>Huidige restsom:</td>
                    <th>
                      {{
                        activeScenarioData.partner.mortgage_situation
                          .remaining_sum | toCurrency(0)
                      }}
                    </th>
                  </tr>
                  <tr>
                    <td>Maandlast maximale hypotheek:</td>
                    <th>
                      {{
                        activeScenarioData.partner.maximum_mortgage
                          .monthly_payments | toCurrency(0)
                      }}
                    </th>
                    <td>Bedrag maximale hypotheek:</td>
                    <th>
                      {{
                        activeScenarioData.partner.maximum_mortgage.total_sum
                          | toCurrency(0)
                      }}
                    </th>
                  </tr>
                  <tr>
                    <td>Verschil:</td>
                    <th>
                      {{
                        (activeScenarioData.partner.mortgage_situation
                          .monthly_payments -
                          activeScenarioData.partner.maximum_mortgage
                            .monthly_payments)
                          | toCurrency(0)
                      }}
                    </th>
                    <td>Verschil:</td>
                    <th>
                      {{
                        (activeScenarioData.partner.mortgage_situation
                          .remaining_sum -
                          activeScenarioData.partner.maximum_mortgage.total_sum)
                          | toCurrency(0)
                      }}
                    </th>
                  </tr>
                  <tr>
                    <td>Kan betalen:</td>
                    <th>
                      <dd-icon
                        v-if="
                          activeScenarioData.partner.verdict.can_pay_mortgage
                        "
                        icon="mdi mdi-check color-success"
                        size="lg"
                      />
                      <dd-icon
                        v-else
                        icon="mdi mdi-close-circle color-danger"
                        size="lg"
                      />
                      {{
                        activeScenarioData.partner.verdict.can_pay_mortgage
                          ? "Ja"
                          : "Nee"
                      }}
                    </th>
                    <td>Kan verlengen:</td>
                    <th>
                      <dd-icon
                        v-if="
                          activeScenarioData.partner.verdict
                            .can_prolong_mortgage
                        "
                        icon="mdi mdi-check color-success"
                        size="lg"
                      />
                      <dd-icon
                        v-else
                        icon="mdi mdi-close-circle color-danger"
                        size="lg"
                      />
                      {{
                        activeScenarioData.partner.verdict.can_prolong_mortgage
                          ? "Ja"
                          : "Nee"
                      }}
                    </th>
                  </tr>
                </tbody>
              </dd-custom-table>
            </div>
            <a-alert
              v-if="warningDescription[activeScenario]"
              type="warning"
              showIcon
              :message="warningDescription[activeScenario]"
            />
            <a-alert
              v-if="possibilityDescription[activeScenario]"
              class="mt-3"
              type="success"
              showIcon
              :message="possibilityDescription[activeScenario]"
            />
          </div>
        </div>
        <p v-else>
          Geen resultaat. Klik op "Bereken" om de scenario's op te halen.
        </p>

        <!-- Error messages -->
        <div v-if="errorMessage" style="color: red; margin-top: 16px">
          {{ errorMessage }}
        </div>
      </div>
    </dd-header-card>
  </div>
</template>

<script>
import analysisApi from "~/api/analysis"; // Assume you have filters like toCurrency, toDate, etc. in your project
import { toCurrency, toDate, toPercentage } from "~/plugins/string-helpers";

export default {
  name: "ScenariosPage",
  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      toDate,
      toCurrency,
      calculationValues: null, // Data from the initial GET
      scenariosResult: null, // Data from the scenarios calculation
      calcLoading: false,
      errorMessage: null,

      repaymentTypeTranslation: {
        annuity: "Annuiteit",
        linear: "Lineair",
        life_insurance: "Levensverzekering",
        interest_only: "Aflossingsvrij",
        investment_mortgage: "Beleggingshypotheek",
        savings: "Spaarhypotheek",
        bank_savings: "Bankspaar",
      },

      // Which scenario is currently selected?
      activeScenario: null,

      // Map scenario keys to user-friendly labels
      scenarios: {
        retirement: "Pensioen",
        incapacitated: "Arbeidsongeschiktheid",
        unemployment: "Werkloosheid",
        break_up: "Einde relatie",
        death: "Overlijden",
      },

      // Additional objects holding descriptions & icons
      scenarioEffectDescription: {
        retirement: "Als je met pensioen gaat kan je",
        death: "Als je overlijdt kan je partner",
        incapacitated: "Als je langdurig ziek wordt kan je",
        unemployment: "Als je werkloos raakt kan je",
        break_up: "Als jullie relatie eindigt kan je",
        retirementPartner: "Als je partner met pensioen gaat kan je",
        deathPartner: "Als je partner overlijdt kan je",
        incapacitatedPartner: "Als je partner langdurig ziek wordt kan je",
        unemploymentPartner: "Als je partner werkloos raakt kan je",
        break_upPartner: "Als jullie relatie eindigt kan je",
      },
      warningDescription: {
        retirement: null,
        death: "Let op: er is geen rekening gehouden met een eventuele ORV.",
        incapacitated:
          "Let op: er is geen rekening gehouden met een eventuele arbeidsongeschiktheidsverzekering.",
        unemployment:
          "Let op: er is geen rekening gehouden met een eventuele woonlastenverzekering.",
        break_up: null,
      },
      possibilityDescription: {
        retirement:
          "Voor meer zekerheid kun je een lijfrenteregeling aanvragen...",
        death:
          "Voor meer zekerheid kun je een overlijdensrisicoverzekering (ORV) afsluiten...",
        incapacitated:
          "Voor meer zekerheid kun je een arbeidsongeschiktheidsverzekering afsluiten...",
        unemployment:
          "Neem contact met ons op om te bespreken hoe je niet voor verrassingen komt te staan...",
        break_up:
          "Meer weten? Neem contact met ons op door op de knop hieronder te klikken...",
      },
      scenarioIcons: {
        retirement: "smile", // antd icon name (or your own custom icon)
        death: "frown",
        incapacitated: "alert",
        unemployment: "warning",
        break_up: "heart",
      },
      downloadingPDF: false,
    };
  },
  created() {
    this.fetchScenariosValues();
  },
  methods: {
    async fetchScenariosValues() {
      try {
        const response = await analysisApi.getScenarioCalculationValues({
          customer_id: this.customerId,
        });
        this.calculationValues = response.calculationValues;
        this.scenariosResult = response.lastSavedResult;
      } catch (error) {
        this.errorMessage =
          "Er is een fout opgetreden bij het ophalen van de scenario-waarden.";
        console.error(error);
      }
    },

    async calculateScenarios() {
      this.calcLoading = true;
      this.errorMessage = null;
      try {
        const response = await analysisApi.saveScenarios({
          customer_id: this.customerId,
        });
        // We assume the full scenarios object is just returned as 'response'
        this.scenariosResult = response.data;
        // By default, select the first scenario if any exist
        const keys = Object.keys(response.data);
        if (keys.length > 0) {
          this.activeScenario = keys[0];
        }
      } catch (error) {
        this.errorMessage =
          "Er is een fout opgetreden bij het berekenen van de scenario's.";
        console.error(error);
      } finally {
        this.calcLoading = false;
      }
    },
    async exportPDF() {
      this.downloadingPDF = true;

      try {
        let response = await analysisApi.scenariosPDF({
          customer_id: this.customerId,
        });

        const url = window.URL.createObjectURL(
          new Blob([Buffer.from(response.data, "base64")], {
            type: "application/pdf",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `kan_ik_blijven_wonen.pdf`); //or any other extension
        document.body.appendChild(link);

        link.click();
      } catch (e) {
      } finally {
        this.downloadingPDF = false;
      }
    },
  },
  computed: {
    loanPartColumns() {
      return [
        {
          title: "Type aflossing",
          width: "30px",
          key: (part) => {
            return (
              this.repaymentTypeTranslation[part.repayment_type] || "Onbekend"
            );
          },
        },
        {
          title: "Huidig bedrag",
          width: "30px",
          key: (part) => {
            return toCurrency(part.current_amount, 0);
          },
        },
        {
          title: "Rente (%)",
          width: "30px",
          key: (part) => {
            return toPercentage(part.interest_rate, 2);
          },
        },
        {
          title: "Looptijd (mnd)",
          width: "30px",
          key: "duration_in_months",
        },
        {
          title: "RVP (mnd)",
          width: "30px",
          key: "remaining_interest_period_in_months",
        },
        {
          title: "Aftrekbare periode (mnd)",
          width: "30px",
          key: "deductible_period_in_months",
        },
      ];
    },

    // Which scenario keys to show (only those that exist in the result + in the 'scenarios' map)
    scenariosToShow() {
      if (!this.scenariosResult) return [];
      return Object.keys(this.scenariosResult).filter((key) =>
        this.scenarios.hasOwnProperty(key)
      );
    },

    // The scenario data for the currently active scenario
    activeScenarioData() {
      if (!this.scenariosResult || !this.activeScenario) return null;
      return this.scenariosResult[this.activeScenario];
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/_default.scss";

/* Example highlight when a scenario is active */
.active {
  border: 1px solid $color-primary !important;
  box-shadow: 0 0 3px $color-primary;
}

table th,
td {
  padding-left: 22px !important;
}
</style>
